import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { EntityService, SharedService } from '@app/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { HyperpureOrder } from '../../../hyperpure/interfaces';
import { HyperpureOrderService } from '../../../hyperpure/services/hyperpure-order.service';
import { DmartOrderService } from '../../services/dmart-order.service';
import { PurchaseOrderService } from '../../services/purchase-order.service';
import { DeliveritOrder, DeliveritOrderService } from '../../services/deliverit-order.service';

@Component({
    selector: 'app-pm-orders',
    templateUrl: './pm-orders.component.html',
    styleUrls: ['./pm-orders.component.scss']
})
export class PmOrdersComponent implements OnInit, OnDestroy {

    selectedEntityId = '';

    // Sub-tab
    activeSubTab: 'purchaseOrders' | 'hyperpureOrders' | 'udaanOrders' | 'dmartOrders' | 'deliveritOrders' = 'purchaseOrders';

    // Purchase Orders (Local Suppliers)
    purchaseOrders: any[] = [];
    isPoLoading = false;
    poPagination = {
        page: 1,
        pageSize: 10,
        totalItems: 0,
        totalPages: 0
    };
    poSearchQuery = '';

    // Hyperpure Orders
    hyperpureOrders: HyperpureOrder[] = [];
    isHpLoading = false;
    hpPagination = {
        currentPage: 1,
        pageSize: 20,
        totalOrders: 0,
        totalPages: 0
    };
    hpSearchTerm = '';
    hpSelectedStatus = '';
    hpSortBy = 'order_placed_at';
    hpSortOrder: 'asc' | 'desc' = 'desc';
    hpStatusOptions = [
        { label: 'All Status', value: '' },
        { label: 'Placed', value: 'PLACED' },
        { label: 'Delivered', value: 'DELIVERED' },
        { label: 'Cancelled', value: 'CANCELLED' },
        { label: 'Pending', value: 'PENDING' }
    ];

    showHpOrderDetails = false;
    selectedHpOrder: HyperpureOrder | null = null;

    // DMart Orders
    dmartOrders: any[] = [];
    isDmartLoading = false;
    dmartPagination = {
        page: 1,
        pageSize: 10,
        totalRecords: 0,
        totalPages: 0
    };
    dmartSearchQuery = '';

    // DeliverIt Orders
    deliveritOrders: DeliveritOrder[] = [];
    isDeliveritLoading = false;
    deliveritPagination = {
        currentPage: 1,
        pageSize: 20,
        totalOrders: 0,
        totalPages: 0
    };
    deliveritSearchTerm = '';
    deliveritSelectedStatus = '';
    deliveritSortBy = 'order_date';
    deliveritSortOrder: 'asc' | 'desc' = 'desc';
    deliveritStatusOptions = [
        { label: 'All Status', value: '' },
        { label: 'New', value: 'NEW' },
        { label: 'Pick-Pack', value: 'PICK-PACK' },
        { label: 'Invoicing', value: 'INVOICING' },
        { label: 'Assigned', value: 'ASSIGNED' },
        { label: 'Out For Delivery', value: 'OUT FOR DELIVERY' },
        { label: 'Delivered', value: 'DELIVERED' },
        { label: 'Returned', value: 'RETURNED' },
        { label: 'Closed', value: 'CLOSED' },
        { label: 'Cancelled', value: 'CANCELLED' }
    ];

    private destroy$ = new Subject<void>();

    constructor(
        private entityService: EntityService,
        private sharedService: SharedService,
        private purchaseOrderService: PurchaseOrderService,
        private hyperpureOrderService: HyperpureOrderService,
        private dmartOrderService: DmartOrderService,
        private deliveritOrderService: DeliveritOrderService
    ) { }

    /**
     * Initializes the component and loads purchase orders for the selected entity.
     */
    ngOnInit(): void {
        const selectedEntity = this.entityService.getSingleSelectedEntity();
        if (selectedEntity) {
            this.selectedEntityId = selectedEntity.id;
        }

        this.listenToEntityChanges();
        this.fetchPurchaseOrders();
    }

    /**
     * Reloads the open orders tab when the header entity changes.
     * The dropdown updates the shared selection, but this page only read it once on init.
     */
    listenToEntityChanges(): void {
        this.entityService.singleSelectedEntity$
            .pipe(takeUntil(this.destroy$))
            .subscribe(entity => {
                const id = entity?.id || (typeof entity === 'string' ? entity : '');
                if (!id || id === this.selectedEntityId) {
                    return;
                }

                this.selectedEntityId = id;
                this.reloadActiveTab();
            });
    }

    private reloadActiveTab(): void {
        this.closeHpOrderDetails();

        if (this.activeSubTab === 'purchaseOrders') {
            this.purchaseOrders = [];
            this.poPagination.page = 1;
            this.fetchPurchaseOrders(1);
            return;
        }

        if (this.activeSubTab === 'hyperpureOrders') {
            this.hyperpureOrders = [];
            this.hpPagination.currentPage = 1;
            this.fetchHyperpureOrders();
            return;
        }

        if (this.activeSubTab === 'dmartOrders') {
            this.dmartOrders = [];
            this.dmartPagination.page = 1;
            this.fetchDmartOrders(1);
            return;
        }

        if (this.activeSubTab === 'deliveritOrders') {
            this.deliveritOrders = [];
            this.deliveritPagination.currentPage = 1;
            this.deliveritPagination.totalOrders = 0;
            this.deliveritPagination.totalPages = 0;
            this.fetchDeliveritOrders();
        }
    }

    /**
     * Cleanup on component destruction.
     */
    ngOnDestroy(): void {
        this.closeHpOrderDetails();
        this.destroy$.next();
        this.destroy$.complete();
    }

    // ── Sub-Tab ──

    /**
     * Handles switching between Purchase Orders and Hyperpure Orders tabs.
     * @param subTab The sub-tab to switch to.
     */
    onSubTabChange(subTab: 'purchaseOrders' | 'hyperpureOrders' | 'udaanOrders' | 'dmartOrders' | 'deliveritOrders'): void {
        this.activeSubTab = subTab;
        if (subTab === 'purchaseOrders') {
            this.fetchPurchaseOrders();
        } else if (subTab === 'hyperpureOrders') {
            this.fetchHyperpureOrders();
        } else if (subTab === 'udaanOrders') {
            // Placeholder for Udaan orders fetch
            this.purchaseOrders = [];
        } else if (subTab === 'dmartOrders') {
            this.fetchDmartOrders();
        } else if (subTab === 'deliveritOrders') {
            this.fetchDeliveritOrders();
        }
    }

    // ── Purchase Orders ──

    /**
     * Fetches purchase orders from the local supplier PO service.
     * @param page The page number to fetch.
     */
    fetchPurchaseOrders(page: number = 1): void {
        if (!this.selectedEntityId) return;

        const entityId = this.selectedEntityId;
        this.isPoLoading = true;
        this.poPagination.page = page;

        const params: any = {
            page: this.poPagination.page,
            pageSize: this.poPagination.pageSize,
            search: this.poSearchQuery
        };

        this.purchaseOrderService.getPurchaseOrders(params, entityId)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                (response: any) => {
                    if (entityId !== this.selectedEntityId) {
                        return;
                    }

                    const items = response.items || response.data || [];
                    this.purchaseOrders = this.mapPurchaseOrderResponse(items);

                    const pagination = response.pagination || {};
                    this.poPagination.totalItems = pagination.total_items || pagination.totalItems || 0;
                    this.poPagination.totalPages = pagination.total_pages || pagination.totalPages || 0;

                    this.isPoLoading = false;
                },
                (error: any) => {
                    if (entityId !== this.selectedEntityId) {
                        return;
                    }

                    console.error('Failed to fetch purchase orders', error);
                    this.sharedService.errorAlert('Failed to load purchase orders');
                    this.isPoLoading = false;
                }
            );
    }

    /**
     * Maps raw purchase order response to a flatter structure for UI consumption.
     * @param items Raw PO items from API.
     * @returns Mapped PO items.
     */
    private mapPurchaseOrderResponse(items: any[]): any[] {
        return items.map(item => ({
            ...item,
            poNumber: item.po_number || item.poNumber,
            supplierName: item.supplier_name || item.supplierName,
            supplierAlias: item.supplier_alias || item.supplierAlias,
            totalAmount: item.total_amount || item.totalAmount,
        }));
    }

    /**
     * Handles pagination change for purchase orders.
     * @param page New page number.
     */
    onPoPageChange(page: number): void {
        this.poPagination.page = page;
        this.fetchPurchaseOrders(page);
    }

    /**
     * Resets pagination and fetches purchase orders based on search query.
     */
    onPoSearch(): void {
        this.poPagination.page = 1;
        this.fetchPurchaseOrders(1);
    }

    /**
     * Resets pagination and fetches purchase orders when page size changes.
     */
    onPoPageSizeChange(pageSize: number): void {
        this.poPagination.pageSize = pageSize;
        this.poPagination.page = 1;
        this.fetchPurchaseOrders(1);
    }




    // ── Hyperpure Orders ──

    /**
     * Fetches Hyperpure specific orders using the hyperpure order service.
     */
    fetchHyperpureOrders(): void {
        if (!this.selectedEntityId) return;

        const entityId = this.selectedEntityId;
        this.isHpLoading = true;
        const params = {
            page: this.hpPagination.currentPage,
            limit: this.hpPagination.pageSize,
            search: this.hpSearchTerm,
            sortBy: this.hpSortBy,
            sortOrder: this.hpSortOrder,
            status: this.hpSelectedStatus,
            entityId
        };

        this.hyperpureOrderService.getOrders(params)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response) => {
                    if (entityId !== this.selectedEntityId) {
                        return;
                    }

                    if (response.success) {
                        this.hyperpureOrders = (response.data.orders || []).map(order => this.normalizeHpOrder(order));
                        this.hpPagination.totalOrders = response.data.pagination.total;
                        this.hpPagination.totalPages = response.data.pagination.total_pages || response.data.pagination.totalPages;
                    }
                    this.isHpLoading = false;
                },
                error: (error) => {
                    if (entityId !== this.selectedEntityId) {
                        return;
                    }

                    console.error('Error loading Hyperpure orders:', error);
                    this.isHpLoading = false;
                }
            });
    }

    /**
     * Triggers a new Hyperpure order fetch with reset pagination on search.
     */
    onHpSearch(): void {
        this.hpPagination.currentPage = 1;
        this.fetchHyperpureOrders();
    }

    /**
     * Triggers a new Hyperpure order fetch when status filter changes.
     */
    onHpStatusChange(): void {
        this.hpPagination.currentPage = 1;
        this.fetchHyperpureOrders();
    }

    /**
     * Handles sorting for the Hyperpure orders table.
     * @param column Column key to sort by.
     */
    onHpSort(column: string): void {
        if (this.hpSortBy === column) {
            this.hpSortOrder = this.hpSortOrder === 'asc' ? 'desc' : 'asc';
        } else {
            this.hpSortBy = column;
            this.hpSortOrder = 'desc';
        }
        this.fetchHyperpureOrders();
    }

    /**
     * Handles pagination change for Hyperpure orders.
     * @param page New page number.
     */
    onHpPageChange(page: number): void {
        this.hpPagination.currentPage = page;
        this.fetchHyperpureOrders();
    }

    /**
     * Resets pagination and fetches Hyperpure orders when page size changes.
     */
    onHpPageSizeChange(pageSize: number): void {
        this.hpPagination.pageSize = pageSize;
        this.hpPagination.currentPage = 1;
        this.fetchHyperpureOrders();
    }

    /**
     * Picks the first defined, non-empty value. Needed because `s3` in API keys
     * is not fully camelCased by the case interceptor (`combinedInvoiceS3_key`).
     */
    private firstHpValue(...values: any[]): any {
        for (const value of values) {
            if (value !== undefined && value !== null && value !== '') {
                return value;
            }
        }
        return null;
    }

    /**
     * Normalizes Hyperpure order fields so templates can rely on camelCase keys.
     */
    private normalizeHpOrder(order: any): HyperpureOrder {
        if (!order) {
            return order;
        }

        const orderWithoutOtp = { ...order };
        delete orderWithoutOtp.deliveryOtp;
        delete orderWithoutOtp.delivery_otp;

        const invoices = (orderWithoutOtp.invoices || []).map((invoice: any) => ({
            ...invoice,
            invoiceNumber: this.firstHpValue(invoice.invoiceNumber, invoice.invoice_number),
            invoiceType: this.firstHpValue(invoice.invoiceType, invoice.invoice_type),
            invoicePath: this.firstHpValue(invoice.invoicePath, invoice.invoice_path),
            storedS3Path: this.firstHpValue(invoice.storedS3Path, invoice.storedS3_path, invoice.stored_s3_path),
            storedS3Key: this.firstHpValue(invoice.storedS3Key, invoice.storedS3_key, invoice.stored_s3_key)
        }));

        const orderItems = (orderWithoutOtp.orderItems || orderWithoutOtp.order_items || []).map((item: any) => ({
            ...item,
            productName: this.firstHpValue(item.productName, item.product_name),
            orderedQuantity: this.firstHpValue(item.orderedQuantity, item.ordered_quantity) ?? 0,
            invoicedQuantity: this.firstHpValue(item.invoicedQuantity, item.invoiced_quantity) ?? 0,
            pricePerUnit: this.firstHpValue(item.pricePerUnit, item.price_per_unit) ?? 0,
            orderedQuantityPrice: this.firstHpValue(item.orderedQuantityPrice, item.ordered_quantity_price) ?? 0,
            invoicedQuantityPrice: this.firstHpValue(item.invoicedQuantityPrice, item.invoiced_quantity_price) ?? 0,
            discount: item.discount ?? 0,
            itemType: this.firstHpValue(item.itemType, item.item_type),
            hsnCode: this.firstHpValue(item.hsnCode, item.hsn_code),
            gstRate: this.firstHpValue(item.gstRate, item.gst_rate) ?? 0,
            subUom: this.firstHpValue(item.subUom, item.sub_uom),
            subUomCount: this.firstHpValue(item.subUomCount, item.sub_uom_count)
        }));

        return {
            ...orderWithoutOtp,
            orderNumber: this.firstHpValue(orderWithoutOtp.orderNumber, orderWithoutOtp.order_number),
            orderPlacedAt: this.firstHpValue(order.orderPlacedAt, order.order_placed_at),
            targetDeliveryDate: this.firstHpValue(order.targetDeliveryDate, order.target_delivery_date),
            orderStatus: this.firstHpValue(order.orderStatus, order.order_status),
            paymentStatus: this.firstHpValue(order.paymentStatus, order.payment_status),
            paymentMode: this.firstHpValue(order.paymentMode, order.payment_mode),
            totalOrderedValue: this.firstHpValue(order.totalOrderedValue, order.total_ordered_value) ?? 0,
            totalInvoicedValue: this.firstHpValue(order.totalInvoicedValue, order.total_invoiced_value) ?? 0,
            orderUnpaidAmount: this.firstHpValue(order.orderUnpaidAmount, order.order_unpaid_amount) ?? 0,
            buyerOutletName: this.firstHpValue(order.buyerOutletName, order.buyer_outlet_name),
            buyerOutletId: this.firstHpValue(order.buyerOutletId, order.buyer_outlet_id),
            deliveryTimeSlot: this.firstHpValue(order.deliveryTimeSlot, order.delivery_time_slot),
            totalOrderWeight: this.firstHpValue(order.totalOrderWeight, order.total_order_weight),
            orderInstructions: this.firstHpValue(order.orderInstructions, order.order_instructions),
            combinedInvoiceS3Key: this.firstHpValue(
                order.combinedInvoiceS3Key,
                order.combinedInvoiceS3_key,
                order.combined_invoice_s3_key
            ),
            combinedInvoiceS3Path: this.firstHpValue(
                order.combinedInvoiceS3Path,
                order.combinedInvoiceS3_path,
                order.combined_invoice_s3_path
            ),
            combinedInvoiceUrl: this.firstHpValue(order.combinedInvoiceUrl, order.combined_invoice_url),
            combinedInvoicePageCount: this.firstHpValue(
                order.combinedInvoicePageCount,
                order.combined_invoice_page_count
            ) ?? 0,
            invoices,
            orderItems
        };
    }

    /**
     * Returns true when the order has a stored combined invoice in our S3 bucket.
     * @param order The Hyperpure order object.
     */
    hasHpInvoice(order: HyperpureOrder | any): boolean {
        return !!(order?.combinedInvoiceS3Key
            || order?.combinedInvoiceS3_key
            || order?.combined_invoice_s3_key);
    }

    /**
     * Opens the Hyperpure order details modal.
     * @param order The Hyperpure order object.
     */
    openHpOrderDetails(order: HyperpureOrder): void {
        this.selectedHpOrder = order;
        this.showHpOrderDetails = true;
        this.syncHpModalScrollLock();
    }

    /**
     * Closes the Hyperpure order details modal.
     */
    closeHpOrderDetails(): void {
        this.showHpOrderDetails = false;
        this.selectedHpOrder = null;
        this.syncHpModalScrollLock();
    }

    /**
     * Locks body scroll while any Hyperpure modal is open.
     */
    private syncHpModalScrollLock(): void {
        document.body.style.overflow = this.showHpOrderDetails ? 'hidden' : '';
    }

    /**
     * Friendly label for Hyperpure invoice types.
     */
    formatHpInvoiceType(type: string): string {
        const labels: { [key: string]: string } = {
            'NON-TAXABLE_GOODS': 'Non-taxable goods',
            'TAXABLE_GOODS_AND_SERVICES': 'Taxable goods & services',
            'ORDER_DELIVERY_CHALLAN': 'Delivery challan'
        };
        return labels[type] || type || '-';
    }

    /**
     * Display name for an order line item.
     */
    formatHpItemName(item: any): string {
        const name = item?.productName || item?.product_name || '';
        const type = item?.itemType || item?.item_type;
        if (type === 'DELIVERY_CHARGE' || name === 'DELIVERY_CHARGE') {
            return 'Delivery Charge';
        }
        return name || '-';
    }

    /**
     * Opens the stored Hyperpure invoice in a new browser tab.
     * @param order The Hyperpure order object.
     */
    viewHpInvoice(order: HyperpureOrder): void {
        if (!order?.orderNumber || !this.hasHpInvoice(order)) {
            this.sharedService.warningAlert('Invoice not available yet');
            return;
        }

        const previewWindow = window.open('', '_blank');
        if (!previewWindow) {
            this.sharedService.errorAlert('Please allow pop-ups to view the invoice');
            return;
        }
        previewWindow.document.title = `Invoice ${order.orderNumber}`;

        this.hyperpureOrderService.getInvoiceSignedUrl(order.orderNumber, 'combined', false, this.selectedEntityId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response) => {
                    if (response.success && response.data?.signedUrl) {
                        previewWindow.location.href = response.data.signedUrl;
                        return;
                    }
                    previewWindow.close();
                    this.sharedService.warningAlert(response.message || 'Invoice not available yet');
                },
                error: (error) => {
                    console.error('Failed to open Hyperpure invoice', error);
                    previewWindow.close();
                    this.sharedService.errorAlert('Failed to open invoice');
                }
            });
    }

    @HostListener('document:keydown.escape')
    onEscapeCloseInvoice(): void {
        if (this.showHpOrderDetails) {
            this.closeHpOrderDetails();
        }
    }

    /**
     * Returns the appropriate CSS class for a status badge.
     * @param status The order status string.
     */
    getHpStatusBadgeClass(status: string): string {
        const statusMap: { [key: string]: string } = {
            'PLACED': 'badge-primary',
            'DELIVERED': 'badge-success',
            'CANCELLED': 'badge-danger',
            'PENDING': 'badge-warning'
        };
        return statusMap[status] || 'badge-secondary';
    }

    /**
     * Returns the CSS class for a Hyperpure payment status badge.
     */
    getHpPaymentStatusBadgeClass(status: string): string {
        return status === 'paid' ? 'badge-success' : 'badge-warning';
    }

    /**
     * Formats a date string or object into a localized Indian date format.
     * @param date Date to format.
     */
    formatHpDate(date: Date | string): string {
        if (!date) return '-';
        const d = new Date(date);
        return d.toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    }

    /**
     * Formats a number into Indian currency (Rupees) string.
     * @param amount Amount to format.
     */
    formatHpCurrency(amount: number): string {
        if (amount === null || amount === undefined) return '₹0.00';
        return `₹${amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }

    /**
     * Calculates the range of pages to display in the Hyperpure pagination UI.
     */
    get hpPaginationPages(): number[] {
        const pages: number[] = [];
        const maxPagesToShow = 5;
        let startPage = Math.max(1, this.hpPagination.currentPage - Math.floor(maxPagesToShow / 2));
        let endPage = Math.min(this.hpPagination.totalPages, startPage + maxPagesToShow - 1);
        if (endPage - startPage < maxPagesToShow - 1) {
            startPage = Math.max(1, endPage - maxPagesToShow + 1);
        }
        for (let i = startPage; i <= endPage; i++) {
            pages.push(i);
        }
        return pages;
    }

    // ── DMart Orders ──

    /**
     * Fetches DMart orders from the DMart order service.
     * @param page The page number to fetch.
     */
    fetchDmartOrders(page: number = 1): void {
        if (!this.selectedEntityId) return;

        const entityId = this.selectedEntityId;
        this.isDmartLoading = true;
        this.dmartPagination.page = page;

        const params: any = {
            page: this.dmartPagination.page,
            pageSize: this.dmartPagination.pageSize,
            search: this.dmartSearchQuery
        };

        this.dmartOrderService.getOrders(params)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                (response: any) => {
                    if (entityId !== this.selectedEntityId) {
                        return;
                    }

                    if (response.success) {
                        this.dmartOrders = response.data.orders || [];
                        this.dmartPagination.totalRecords = response.data.pagination.totalRecords || 0;
                        this.dmartPagination.totalPages = response.data.pagination.totalPages || 0;
                    } else {
                        this.dmartOrders = [];
                        this.dmartPagination.totalRecords = 0;
                        this.dmartPagination.totalPages = 0;
                    }
                    this.isDmartLoading = false;
                },
                (error: any) => {
                    if (entityId !== this.selectedEntityId) {
                        return;
                    }

                    console.error('Failed to fetch DMart orders', error);
                    this.sharedService.errorAlert('Failed to load DMart orders');
                    this.dmartOrders = [];
                    this.isDmartLoading = false;
                }
            );
    }

    /**
     * Handles pagination change for DMart orders.
     * @param page New page number.
     */
    onDmartPageChange(page: number): void {
        this.dmartPagination.page = page;
        this.fetchDmartOrders(page);
    }

    /**
     * Resets pagination and fetches DMart orders based on search query.
     */
    onDmartSearch(): void {
        this.dmartPagination.page = 1;
        this.fetchDmartOrders(1);
    }

    /**
     * Resets pagination and fetches DMart orders when page size changes.
     */
    onDmartPageSizeChange(pageSize: number): void {
        this.dmartPagination.pageSize = pageSize;
        this.dmartPagination.page = 1;
        this.fetchDmartOrders(1);
    }

    /**
     * Returns the appropriate CSS class for a DMart status badge.
     * @param status The order status code.
     */
    getDmartStatusBadgeClass(status: string): string {
        const statusMap: { [key: string]: string } = {
            'd': 'badge-success',      // Delivered
            'p': 'badge-primary',      // Placed
            'c': 'badge-danger',       // Cancelled
            'pending': 'badge-warning' // Pending
        };
        return statusMap[status.toLowerCase()] || 'badge-secondary';
    }

    /**
     * Formats a DMart date string (DD-MMM-YYYY) to display format.
     * @param date Date string to format.
     */
    formatDmartDate(date: string): string {
        if (!date) return '-';
        return date; // Already in format like "23-Feb-2026"
    }

    /**
     * Formats a number into Indian currency (Rupees) string.
     * @param amount Amount to format.
     */
    formatDmartCurrency(amount: number): string {
        if (amount === null || amount === undefined) return '₹0.00';
        return `₹${amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }

    /**
     * Calculates the range of pages to display in the DMart pagination UI.
     */
    get dmartPaginationPages(): number[] {
        const pages: number[] = [];
        const maxPagesToShow = 5;
        let startPage = Math.max(1, this.dmartPagination.page - Math.floor(maxPagesToShow / 2));
        let endPage = Math.min(this.dmartPagination.totalPages, startPage + maxPagesToShow - 1);
        if (endPage - startPage < maxPagesToShow - 1) {
            startPage = Math.max(1, endPage - maxPagesToShow + 1);
        }
        for (let i = startPage; i <= endPage; i++) {
            pages.push(i);
        }
        return pages;
    }

    /**
     * Downloads DMart invoice for a specific order
     * @param order The DMart order object
     */
    onDownloadDmartInvoice(order: any): void {
        if (!order || !order.orderId) {
            this.sharedService.errorAlert('Invalid order');
            return;
        }

        // Set loading state for this specific order
        order.isDownloading = true;

        this.dmartOrderService.downloadInvoice(order.orderId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response: any) => {
                    try {
                        // Extract blob from response
                        const blob = response.body;
                        
                        if (!blob || blob.size === 0) {
                            throw new Error('Empty file received');
                        }

                        // Get filename from Content-Disposition header or use default
                        let filename = `DMart_Invoice_${order.orderId}.pdf`;
                        const contentDisposition = response.headers.get('Content-Disposition');
                        if (contentDisposition) {
                            const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
                            if (matches != null && matches[1]) {
                                filename = matches[1].replace(/['"]/g, '');
                            }
                        }

                        // Create download link and trigger download
                        const url = window.URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.href = url;
                        link.download = filename;
                        link.style.display = 'none';
                        document.body.appendChild(link);
                        link.click();
                        
                        // Cleanup
                        setTimeout(() => {
                            document.body.removeChild(link);
                            window.URL.revokeObjectURL(url);
                        }, 100);

                        order.isDownloading = false;
                        this.sharedService.successAlert('Invoice downloaded successfully');
                    } catch (error) {
                        console.error('Error processing invoice download:', error);
                        order.isDownloading = false;
                        this.sharedService.errorAlert('Failed to process invoice download');
                    }
                },
                error: (error: any) => {
                    console.error('Failed to download DMart invoice', error);
                    order.isDownloading = false;
                    
                    let errorMessage = 'Failed to download invoice';
                    if (error.status === 404) {
                        errorMessage = 'Invoice not found';
                    } else if (error.status === 401 || error.status === 403) {
                        errorMessage = 'Unauthorized to download invoice';
                    } else if (error.status === 500) {
                        errorMessage = 'Server error. Please try again later';
                    }
                    
                    this.sharedService.errorAlert(errorMessage);
                }
            });
    }

    // ── DeliverIt Orders ──

    /**
     * Fetches DeliverIt orders using the DeliverIt order service.
     */
    fetchDeliveritOrders(): void {
        if (!this.selectedEntityId) return;

        const entityId = this.selectedEntityId;
        this.isDeliveritLoading = true;
        const params = {
            page: this.deliveritPagination.currentPage,
            limit: this.deliveritPagination.pageSize,
            search: this.deliveritSearchTerm,
            sort_by: this.deliveritSortBy,
            sort_order: this.deliveritSortOrder,
            status: this.deliveritSelectedStatus,
            entityId
        };

        this.deliveritOrderService.getOrders(params)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response) => {
                    if (entityId !== this.selectedEntityId) {
                        return;
                    }

                    if (response.success) {
                        this.deliveritOrders = response.data.orders || [];
                        this.deliveritPagination.totalOrders = response.data.pagination?.total || 0;
                        this.deliveritPagination.totalPages = response.data.pagination?.totalPages || 0;
                    } else {
                        this.deliveritOrders = [];
                        this.deliveritPagination.totalOrders = 0;
                        this.deliveritPagination.totalPages = 0;
                    }
                    this.isDeliveritLoading = false;
                },
                error: (error) => {
                    if (entityId !== this.selectedEntityId) {
                        return;
                    }

                    console.error('Error loading DeliverIt orders:', error);
                    this.deliveritOrders = [];
                    this.isDeliveritLoading = false;
                }
            });
    }

    /**
     * Triggers a new DeliverIt order fetch with reset pagination on search.
     */
    onDeliveritSearch(): void {
        this.deliveritPagination.currentPage = 1;
        this.fetchDeliveritOrders();
    }

    /**
     * Triggers a new DeliverIt order fetch when status filter changes.
     */
    onDeliveritStatusChange(): void {
        this.deliveritPagination.currentPage = 1;
        this.fetchDeliveritOrders();
    }

    /**
     * Handles pagination change for DeliverIt orders.
     * @param page New page number.
     */
    onDeliveritPageChange(page: number): void {
        this.deliveritPagination.currentPage = page;
        this.fetchDeliveritOrders();
    }

    /**
     * Resets pagination and fetches DeliverIt orders when page size changes.
     */
    onDeliveritPageSizeChange(pageSize: number): void {
        this.deliveritPagination.pageSize = pageSize;
        this.deliveritPagination.currentPage = 1;
        this.fetchDeliveritOrders();
    }

    /**
     * Triggers an invoice download for a specific DeliverIt order.
     * @param order The DeliverIt order object.
     */
    downloadDeliveritInvoice(order: DeliveritOrder): void {
        const existingUrl = order.invoice?.invoiceUrl;
        if (existingUrl) {
            window.open(existingUrl, '_blank');
            return;
        }

        this.deliveritOrderService.getInvoiceSignedUrl(order.orderId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response) => {
                    if (response.success && response.data.signedUrl) {
                        window.open(response.data.signedUrl, '_blank');
                    } else {
                        this.sharedService.warningAlert(response.message || 'Invoice not available yet');
                    }
                },
                error: (error) => {
                    console.error('Failed to fetch DeliverIt invoice', error);
                    this.sharedService.errorAlert('Failed to download invoice');
                }
            });
    }

    /**
     * Returns the appropriate CSS class for a DeliverIt status badge.
     * @param status The order status string.
     */
    getDeliveritStatusBadgeClass(status: string): string {
        const statusMap: { [key: string]: string } = {
            'NEW': 'badge-primary',
            'PICK-PACK': 'badge-info',
            'INVOICING': 'badge-purple',
            'ASSIGNED': 'badge-secondary',
            'OUT FOR DELIVERY': 'badge-warning',
            'DELIVERED': 'badge-success',
            'RETURNED': 'badge-danger',
            'CLOSED': 'badge-secondary',
            'CANCELLED': 'badge-danger'
        };
        return statusMap[status] || 'badge-secondary';
    }

    /**
     * Formats a date string or object into a localized Indian date format.
     * @param date Date to format.
     */
    formatDeliveritDate(date: Date | string): string {
        if (!date) return '-';
        const d = new Date(date);
        return d.toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    }

    /**
     * Formats a number into Indian currency (Rupees) string.
     * @param amount Amount to format.
     */
    formatDeliveritCurrency(amount: number): string {
        if (amount === null || amount === undefined) return '₹0.00';
        return `₹${amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }

    /**
     * Calculates the range of pages to display in the DeliverIt pagination UI.
     */
    get deliveritPaginationPages(): number[] {
        const pages: number[] = [];
        const maxPagesToShow = 5;
        let startPage = Math.max(1, this.deliveritPagination.currentPage - Math.floor(maxPagesToShow / 2));
        let endPage = Math.min(this.deliveritPagination.totalPages, startPage + maxPagesToShow - 1);
        if (endPage - startPage < maxPagesToShow - 1) {
            startPage = Math.max(1, endPage - maxPagesToShow + 1);
        }
        for (let i = startPage; i <= endPage; i++) {
            pages.push(i);
        }
        return pages;
    }
}
